import type { ReactNode } from "react";
import { Typography, Card, Row, Col, Tag, Image, Divider, Space } from "antd";
import { Icon } from "@iconify/react";

const { Title, Paragraph, Text } = Typography;

const dataCategories: { icon: string; label: string }[] = [
  { icon: "lucide:user", label: "Demographics (name, date of birth, contact info)" },
  { icon: "lucide:stethoscope", label: "Conditions and diagnoses" },
  { icon: "lucide:pill", label: "Medications" },
  { icon: "lucide:test-tube", label: "Lab results and vital signs" },
  { icon: "lucide:syringe", label: "Immunizations" },
  { icon: "lucide:alert-triangle", label: "Allergies" },
  { icon: "lucide:calendar-clock", label: "Visit and encounter history" },
  { icon: "lucide:shield", label: "Insurance coverage information" },
  { icon: "lucide:file-text", label: "Documents and reports (including PDFs and imaging)" },
];

const sidebarModules: { icon: string; label: string; blurb: string }[] = [
  { icon: "lucide:layout-dashboard", label: "Dashboard", blurb: "A single overview of everything connected." },
  { icon: "lucide:clock", label: "Health Timeline", blurb: "Every visit and event, in chronological order." },
  { icon: "lucide:stethoscope", label: "Diagnoses & Health Concerns", blurb: "Conditions a patient has been diagnosed with." },
  { icon: "lucide:history", label: "Complete History", blurb: "The full clinical record in one place." },
  { icon: "lucide:pill", label: "Medications", blurb: "Current and past prescriptions." },
  { icon: "lucide:clipboard-list", label: "Care & Treatment", blurb: "Procedures, treatment plans, and visit notes." },
  { icon: "lucide:shield", label: "Insurance & Cards", blurb: "Coverage details and digital insurance cards." },
  { icon: "lucide:receipt", label: "Claims", blurb: "Insurance claims tied to a patient's care." },
  { icon: "lucide:shield-check", label: "Consent & Privacy", blurb: "Review or revoke what's shared, at any time." },
];

function SectionLabel({ icon, children }: { icon: string; children: string }) {
  return (
    <Space align="center" size={10} className="mb-3">
      <span
        className="flex h-9 w-9 items-center justify-center rounded-xl"
        style={{ background: "rgba(228,0,43,0.1)" }}
      >
        <Icon icon={icon} className="h-[18px] w-[18px]" style={{ color: "#E4002B" }} />
      </span>
      <Title level={2} className="!mb-0 !text-[#0A2947]" style={{ fontSize: 24 }}>
        {children}
      </Title>
    </Space>
  );
}

function FlowStep({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-bold text-white"
          style={{ background: "#E4002B" }}
        >
          {number}
        </span>
        <span className="mt-1 w-px flex-1" style={{ background: "#DAD9D8" }} />
      </div>
      <div className="pb-8">
        <Text strong className="block text-base text-[#0A2947]">
          {title}
        </Text>
        <div className="mt-1.5 max-w-2xl text-[15px] leading-6 text-[#75787B]">{children}</div>
      </div>
    </div>
  );
}

export function AboutPage() {
  return (
    <main className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="border-b border-[#EEF0F3] bg-white">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-6 py-5">
          <img src="/logo.png" alt="MedSyncIQ" className="h-9 w-9 rounded-lg object-contain" />
          <Text strong className="text-lg text-[#0A2947]">
            MedSyncIQ
          </Text>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-12">
        {/* 1. What MedSyncIQ is */}
        <section className="mb-14 text-center">
          <Title level={1} className="!mb-4 !text-[#0A2947]" style={{ fontSize: 36 }}>
            One place for all of a patient's health records
          </Title>
          <Paragraph className="mx-auto max-w-2xl !text-base !leading-7 !text-[#75787B]">
            MedSyncIQ is a patient-facing platform that lets patients connect their health records
            from multiple hospitals and EHR systems into one place, so they can see their complete
            medical history in a single, easy-to-read view.
          </Paragraph>
        </section>

        <Divider />

        {/* 2. Who it's for */}
        <section className="my-14">
          <SectionLabel icon="lucide:user-check">Who It's For</SectionLabel>
          <Card className="!border-[#EEF0F3] !bg-white">
            <Paragraph className="!mb-0 !text-[15px] !leading-7 !text-[#3A3D42]">
              MedSyncIQ is built for <strong>individual patients</strong> who want to view their
              own consolidated medical records in one place. It is <strong>not</strong> intended
              for clinicians, hospital staff, or administrative use — it does not replace a
              hospital's clinical systems.
            </Paragraph>
          </Card>
        </section>

        <Divider />

        {/* 3. What data it accesses */}
        <section className="my-14">
          <SectionLabel icon="lucide:database">What Data It Accesses</SectionLabel>
          <Paragraph className="!mb-5 !text-[15px] !text-[#75787B]">
            When a patient connects a hospital, MedSyncIQ requests access to the following
            categories of health information from that hospital's patient portal:
          </Paragraph>
          <Row gutter={[12, 12]}>
            {dataCategories.map((item) => (
              <Col xs={24} sm={12} key={item.label}>
                <div className="flex h-full items-center gap-3 rounded-xl border border-[#EEF0F3] bg-white px-4 py-3">
                  <Icon icon={item.icon} className="h-[18px] w-[18px] shrink-0" style={{ color: "#E4002B" }} />
                  <Text className="text-sm text-[#3A3D42]">{item.label}</Text>
                </div>
              </Col>
            ))}
          </Row>
        </section>

        <Divider />

        {/* 4. How the connection works + 6. Screenshots, woven into one illustrated walkthrough */}
        <section className="my-14">
          <SectionLabel icon="lucide:workflow">How It Works</SectionLabel>

          <FlowStep number={1} title="Connect and authorize">
            The patient clicks <strong>“Connect”</strong> in MedSyncIQ and selects their hospital
            from a list. They are taken directly to that hospital's own official patient portal to
            log in — <strong>MedSyncIQ never sees or asks for the patient's hospital portal
            password.</strong> On the hospital's site, the patient reviews exactly what
            information will be shared and approves it before anything is sent.
          </FlowStep>

          <FlowStep number={2} title="Records come into MedSyncIQ">
            <p className="mb-4">
              Once approved, the patient is returned to MedSyncIQ and their records begin
              populating automatically. Patients can also manually upload existing documents —
              PDFs, lab reports, or imaging files — at any time.
            </p>
            <Image
              src="/upload.png"
              alt="Screenshot of the MedSyncIQ Upload Records page"
              className="!rounded-xl !border !border-[#EEF0F3]"
              style={{ maxWidth: "100%" }}
            />
            <Text className="mt-2 block text-xs text-[#9CA3AF]">Uploading a record in MedSyncIQ</Text>
          </FlowStep>

          <FlowStep number={3} title="Everything shows up in one dashboard">
            <p className="mb-4">
              Every connected record — from every connected hospital — is organized into a single
              dashboard, so the patient can see their whole health picture in one place instead of
              logging into several separate hospital portals.
            </p>
            <Image
              src="/dashboard.png"
              alt="Screenshot of the MedSyncIQ patient dashboard"
              className="!rounded-xl !border !border-[#EEF0F3]"
              style={{ maxWidth: "100%" }}
            />
            <Text className="mt-2 block text-xs text-[#9CA3AF]">The MedSyncIQ patient dashboard</Text>

            <div className="mt-6 rounded-xl border border-[#EEF0F3] bg-white p-5">
              <Text strong className="mb-3 block text-sm text-[#0A2947]">
                From the dashboard, patients can explore:
              </Text>
              <Row gutter={[12, 12]}>
                {sidebarModules.map((m) => (
                  <Col xs={24} sm={12} key={m.label}>
                    <div className="flex h-full items-start gap-2.5">
                      <Icon icon={m.icon} className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#E4002B" }} />
                      <div>
                        <Text className="block text-sm font-medium text-[#3A3D42]">{m.label}</Text>
                        <Text className="text-xs text-[#9CA3AF]">{m.blurb}</Text>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </FlowStep>

          <div className="flex gap-4">
            <div className="flex flex-col items-center">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-bold text-white"
                style={{ background: "#E4002B" }}
              >
                4
              </span>
            </div>
            <div>
              <Text strong className="block text-base text-[#0A2947]">
                Ask questions about your own records
              </Text>
              <div className="mt-1.5 max-w-2xl text-[15px] leading-6 text-[#75787B]">
                <p className="mb-4">
                  Patients can ask plain-language questions about their own connected records — for
                  example, “What medications am I currently taking?” — and get an answer drawn only
                  from their own data. This is meant to help patients understand their records,
                  not to provide medical advice.
                </p>
                <Image
                  src="/ai.png"
                  alt="Screenshot of the MedSyncIQ AI assistant answering a question about a patient's records"
                  className="!rounded-xl !border !border-[#EEF0F3]"
                  style={{ maxWidth: "100%" }}
                />
                <Text className="mt-2 block text-xs text-[#9CA3AF]">Asking about a record in MedSyncIQ</Text>
              </div>
            </div>
          </div>
        </section>

        <Divider />

        {/* 5. Data handling and privacy */}
        <section className="my-14">
          <SectionLabel icon="lucide:lock">Data Handling & Privacy</SectionLabel>
          <Card className="!border-[#EEF0F3] !bg-white">
            <Space direction="vertical" size={14} className="w-full">
              <Space align="start" size={10}>
                <Icon icon="lucide:shield-check" className="mt-0.5 h-[18px] w-[18px] shrink-0" style={{ color: "#00A98F" }} />
                <Text className="text-[15px] text-[#3A3D42]">
                  Health data is encrypted both in transit and at rest.
                </Text>
              </Space>
              <Space align="start" size={10}>
                <Icon icon="lucide:building-2" className="mt-0.5 h-[18px] w-[18px] shrink-0" style={{ color: "#00A98F" }} />
                <Text className="text-[15px] text-[#3A3D42]">
                  MedSyncIQ is an independent platform and is not affiliated with any hospital or EHR vendor.
                </Text>
              </Space>
              <Space align="start" size={10}>
                <Icon icon="lucide:toggle-left" className="mt-0.5 h-[18px] w-[18px] shrink-0" style={{ color: "#00A98F" }} />
                <Text className="text-[15px] text-[#3A3D42]">
                  Patients can review or revoke a hospital's access at any time from the Consent & Privacy page.
                </Text>
              </Space>
              <Divider className="!my-1" />
              <Space size={16} wrap>
                <a href="/privacy-policy" className="font-semibold" style={{ color: "#E4002B" }}>
                  Privacy Policy
                </a>
                <a href="/terms-of-use" className="font-semibold" style={{ color: "#E4002B" }}>
                  Terms of Use
                </a>
              </Space>
            </Space>
          </Card>
        </section>

        <Divider />

        {/* 7. Contact */}
        <section className="my-14">
          <SectionLabel icon="lucide:mail">Contact</SectionLabel>
          <Card className="!border-[#EEF0F3] !bg-white">
            <Paragraph className="!mb-2 !text-[15px] !text-[#3A3D42]">
              Questions about MedSyncIQ, this page, or how patient data is handled?
            </Paragraph>
            <a href="mailto:support@medsynciq.com" className="text-base font-semibold" style={{ color: "#E4002B" }}>
              support@medsynciq.com
            </a>
          </Card>
        </section>
      </div>

      <footer className="border-t border-[#EEF0F3] py-6 text-center text-xs text-[#9CA3AF]">
        <Tag color="default" className="!border-0 !bg-transparent">
          © {new Date().getFullYear()} MedSyncIQ
        </Tag>
      </footer>
    </main>
  );
}
